import { LotusBlossomPurchase, PurchasesByEmail } from 'api/lotusBlossom/lotusBlossomAPI';
import { formatPhone, RosterContact } from './participants';
import { memberships } from './purchases';

/** One row of the client directory: a contact plus what they've bought. */
export interface Client {
	/** Stable row id, and the key the purchases were matched on. */
	id: string;
	name: string;
	email: string;
	phone: string;
	/** Newest first, as the API returns them. */
	purchases: LotusBlossomPurchase[];
}

function displayName(contact: RosterContact): string {
	const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
	return name || '(no name)';
}

export interface ClientMembership {
	status: string;
	/** ISO date the first membership started. */
	since: string;
	/** ISO date of the next charge, or '' when it will not charge again. */
	periodEnd: string;
	/** True when the membership stops at `periodEnd` rather than renewing. */
	cancelAtPeriodEnd: boolean;
	/** Stripe's id for the current membership; '' from a backend that predates it. */
	subscriptionId: string;
}

export interface ClientSummary {
	/** One-time sessions bought. */
	sessionCount: number;
	/** Present if they have ever held a membership. */
	membership: ClientMembership | null;
}

/**
 * Counts only, deliberately no money. A subscription record carries the monthly
 * price, not what has been billed over its life, so any total would cover
 * one-time purchases and silently omit membership.
 */
export function summarisePurchases(purchases: LotusBlossomPurchase[]): ClientSummary {
	// Signups that never went through aren't memberships, so someone whose only
	// attempt failed has none.
	const subscriptions = memberships(purchases);

	// Status and dates come from the live membership where there is one; someone
	// who lapsed and came back has an older, ended record too. past_due is still
	// live — Stripe is retrying the card.
	const current =
		subscriptions.find((s) => ['active', 'trialing', 'past_due'].includes(s.status)) ??
		subscriptions[0];

	return {
		sessionCount: purchases.filter((p) => p.kind === 'payment').length,
		membership: current
			? {
					status: current.status,
					// The earliest start, so "member since" doesn't reset on a rejoin.
					since: subscriptions.reduce((earliest, s) =>
						s.date < earliest.date ? s : earliest
					).date,
					periodEnd: current.periodEnd ?? '',
					cancelAtPeriodEnd: current.cancelAtPeriodEnd === true,
					subscriptionId: current.subscriptionId ?? '',
				}
			: null,
	};
}

export function filterClients(clients: Client[], search: string): Client[] {
	const term = search.trim().toLowerCase();
	if (!term) return clients;

	// Digits only, so a number typed plainly still matches the grouped display.
	const digits = term.replace(/\D/g, '');

	return clients.filter(
		(c) =>
			c.name.toLowerCase().includes(term) ||
			c.email.toLowerCase().includes(term) ||
			c.phone.toLowerCase().includes(term) ||
			(digits !== '' && c.phone.replace(/\D/g, '').includes(digits))
	);
}

/**
 * A contact with no work email keeps a row but can never match a purchase, since
 * email is the only link between the two systems.
 *
 * The list is kept by hand as well as by checkout, so two contact records can
 * share an email. They are one client — the same purchases would match both —
 * so they collapse into one row, keeping the first name and any phone either has.
 */
export function buildClients(
	contacts: RosterContact[],
	purchasesByEmail: PurchasesByEmail
): Client[] {
	const byId = new Map<string, Client>();

	(contacts ?? []).forEach((contact, index) => {
		const email = contact.workEmail?.trim() ?? '';
		const key = email.toLowerCase();
		const rawPhone = contact.primaryPhone?.trim() || contact.secondaryPhone?.trim() || '';
		// Same grouping as the participants roster, so the tabs match.
		const phone = rawPhone ? formatPhone(rawPhone) : '';
		const id = key || `row:${index}`;

		const existing = byId.get(id);
		if (existing) {
			existing.phone = existing.phone || phone;
			return;
		}

		byId.set(id, {
			id,
			name: displayName(contact),
			email,
			phone,
			purchases: key ? purchasesByEmail[key] ?? [] : [],
		});
	});

	return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}
